/**
 * HealthWatchdog - Auto-recovery service for TrillboardScreen
 *
 * Monitors application health and triggers automatic page reloads
 * when degradation is detected. Designed to improve uptime by
 * recovering from memory leaks, accumulated errors, and network issues.
 */

class HealthWatchdog {
  constructor(healthMonitor) {
    this.healthMonitor = healthMonitor;
    this.checkInterval = 60000; // Check every 60 seconds
    this.intervalId = null;
    this.isRunning = false;

    // Thresholds for triggering reload
    // Note: memoryUsagePercent will be set in start() using device-adaptive value
    this.thresholds = {
      memoryUsagePercent: 85,        // Default, will be overridden by device-adaptive value
      consecutiveErrors: 10,         // Reload if 10+ new errors since last check
      offlineSeconds: 300,           // Reload if offline > 5 minutes
      videoStallCount: 5,            // Reload if 5+ video stalls detected
      contentLoadTimeMs: 10000       // Reload if avg content load > 10 seconds
    };

    // State tracking
    this.lastErrorCount = 0;
    this.lastOnlineTime = Date.now();
    this.videoStallCount = 0;
    this.reloadScheduled = false;
    this.consecutiveHealthyChecks = 0;
    this.lastSoftRecoveryAt = 0;
    this.softRecoveryCooldownMs = 120000; // 2 minutes between soft recoveries
    this.memoryPressureStrikeCount = 0;
    this.requiredMemoryPressureStrikes = 1;

    // Logging prefix
    this.logPrefix = '[HealthWatchdog]';

    // Track event listeners for cleanup
    this._boundListeners = {
      stalled: null,
      waiting: null,
      playing: null
    };

    // Track stall timeouts per video element (WeakMap to avoid memory leak with video elements)
    this._stallTimeouts = new Map();
  }

  /**
   * Get device-adaptive memory threshold
   * Lower thresholds for constrained devices (Fire TV, Chromecast)
   * @private
   */
  _getDefaultMemoryThreshold() {
    const ua = navigator.userAgent || '';

    // Fire TV / Chromecast - most memory constrained
    if (/AFT[A-Z]|Fire TV|CrKey|Chromecast/i.test(ua)) {
      return 75;
    }
    // Samsung Tizen runs YouTube in a cross-origin iframe that can report high
    // heap during normal playback. Treat memory as a late-warning signal only;
    // process restarts are disabled for Tizen below to avoid Samsung review
    // seeing startup/content loops while playback is otherwise healthy.
    if (/Tizen|SMART-TV/i.test(ua)) {
      return 95;
    }
    // LG WebOS / NetCast — kept at the prior default. WebOS doesn't have
    // the same wrapper-driven process-exit escalation path; dropping its
    // threshold without that lever would just trigger reload loops that
    // don't actually free heap.
    if (/webOS|NetCast/i.test(ua)) {
      return 78;
    }
    // Android WebView (tablets in kiosk mode)
    if (/Android.*wv\)/i.test(ua)) {
      return 80;
    }
    // Desktop browsers - most memory available
    return 85;
  }

  _getDefaultCheckInterval() {
    const ua = navigator.userAgent || '';

    if (/Tizen|SMART-TV/i.test(ua)) {
      return 60000;
    }

    return 60000;
  }

  _getRequiredMemoryPressureStrikes() {
    const ua = navigator.userAgent || '';

    if (/Tizen|SMART-TV/i.test(ua)) {
      return 5;
    }

    return 1;
  }

  _isSamsungTizen() {
    const ua = navigator.userAgent || '';
    return /Tizen|SMART-TV/i.test(ua);
  }

  /**
   * Update thresholds from remote config (called by screen app)
   * @param {Object} config - Remote config with optional memoryUsagePercent
   */
  updateFromRemoteConfig(config) {
    if (config && typeof config.memoryUsagePercent === 'number') {
      this.thresholds.memoryUsagePercent = config.memoryUsagePercent;
      console.log(`${this.logPrefix} Memory threshold updated from remote config: ${config.memoryUsagePercent}%`);
    }
  }

  /**
   * Start the watchdog monitoring
   */
  start() {
    if (this.isRunning) {
      console.log(`${this.logPrefix} Already running`);
      return;
    }

    // Apply device-adaptive memory threshold
    this.checkInterval = this._getDefaultCheckInterval();
    this.thresholds.memoryUsagePercent = this._getDefaultMemoryThreshold();
    this.requiredMemoryPressureStrikes = this._getRequiredMemoryPressureStrikes();
    console.log(`${this.logPrefix} Starting health monitoring (interval: ${this.checkInterval}ms, memory threshold: ${this.thresholds.memoryUsagePercent}%)`);
    this.isRunning = true;

    // Set up video stall detection
    this.setupVideoStallDetection();

    // Start periodic health checks
    this.intervalId = setInterval(() => this.checkHealth(), this.checkInterval);

    // Do an initial check after 30 seconds
    setTimeout(() => this.checkHealth(), 30000);
  }

  /**
   * Stop the watchdog monitoring
   */
  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    // Remove video stall event listeners
    if (this._boundListeners.stalled) {
      document.removeEventListener('stalled', this._boundListeners.stalled, true);
      this._boundListeners.stalled = null;
    }
    if (this._boundListeners.waiting) {
      document.removeEventListener('waiting', this._boundListeners.waiting, true);
      this._boundListeners.waiting = null;
    }
    if (this._boundListeners.playing) {
      document.removeEventListener('playing', this._boundListeners.playing, true);
      this._boundListeners.playing = null;
    }

    // Clear all pending stall timeouts
    this._stallTimeouts.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    this._stallTimeouts.clear();

    this.isRunning = false;
    console.log(`${this.logPrefix} Stopped`);
  }

  /**
   * Set up detection for video stalls
   */
  setupVideoStallDetection() {
    // Monitor for video stall events - store listener reference for cleanup
    this._boundListeners.stalled = (event) => {
      if (event.target.tagName === 'VIDEO') {
        // Skip IMA ad container videos — stalls during ads are expected
        if (event.target.closest && event.target.closest('#ima-ad-container')) return;
        this.videoStallCount++;
        console.warn(`${this.logPrefix} Video stall detected (count: ${this.videoStallCount})`);
      }
    };
    document.addEventListener('stalled', this._boundListeners.stalled, true);

    // Monitor for prolonged waiting - store listener reference for cleanup
    this._boundListeners.waiting = (event) => {
      if (event.target.tagName === 'VIDEO') {
        // Skip IMA ad container videos
        if (event.target.closest && event.target.closest('#ima-ad-container')) return;
        const video = event.target;

        // Clear any existing timeout for this video to prevent accumulation
        const existingTimeout = this._stallTimeouts.get(video);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
        }

        // Create new timeout and track it
        const timeoutId = setTimeout(() => {
          // Remove from tracking once executed
          this._stallTimeouts.delete(video);

          // Only count if video is still waiting and not paused
          if (video.readyState < 3 && !video.paused) {
            this.videoStallCount++;
            console.warn(`${this.logPrefix} Video prolonged waiting detected (count: ${this.videoStallCount})`);
          }
        }, 5000);

        this._stallTimeouts.set(video, timeoutId);
      }
    };
    document.addEventListener('waiting', this._boundListeners.waiting, true);

    // Also listen for 'playing' events to clear pending timeouts (video resumed)
    this._boundListeners.playing = (event) => {
      if (event.target.tagName === 'VIDEO') {
        const video = event.target;
        const existingTimeout = this._stallTimeouts.get(video);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
          this._stallTimeouts.delete(video);
        }
      }
    };
    document.addEventListener('playing', this._boundListeners.playing, true);
  }

  /**
   * Main health check function
   */
  async checkHealth() {
    if (!this.healthMonitor) {
      console.warn(`${this.logPrefix} No health monitor available`);
      return;
    }

    try {
      const metrics = await this.healthMonitor.collectHealthMetrics();
      const issues = [];

      // 1. Memory pressure check
      const memoryUsage = parseFloat(metrics.memory?.usagePercent || 0);
      if (memoryUsage > this.thresholds.memoryUsagePercent) {
        this.memoryPressureStrikeCount++;
        issues.push({
          type: 'memory_pressure',
          value: memoryUsage,
          threshold: this.thresholds.memoryUsagePercent,
          strikes: this.memoryPressureStrikeCount,
          requiredStrikes: this.requiredMemoryPressureStrikes,
          message: `Memory usage at ${memoryUsage}%`
        });
      } else {
        this.memoryPressureStrikeCount = 0;
      }

      // 2. Error accumulation check - track for logging only, NOT reload trigger
      // IMA ad errors and other transient errors should not trigger reloads.
      // Removed error_spike reload trigger - too aggressive and causes reload loops.
      const currentErrorCount = metrics.content?.errorCount || 0;
      const newErrors = currentErrorCount - this.lastErrorCount;
      this.lastErrorCount = currentErrorCount;
      if (newErrors > 0) {
        console.log(`${this.logPrefix} ${newErrors} new errors since last check (not triggering reload)`);
      }

      // 3. Network offline check
      if (!metrics.network?.online) {
        const offlineSeconds = (Date.now() - this.lastOnlineTime) / 1000;
        if (offlineSeconds > this.thresholds.offlineSeconds) {
          issues.push({
            type: 'prolonged_offline',
            value: offlineSeconds,
            threshold: this.thresholds.offlineSeconds,
            message: `Offline for ${Math.round(offlineSeconds)} seconds`
          });
        }
      } else {
        this.lastOnlineTime = Date.now();
      }

      // 4. Video stall check - track for logging only, NOT reload trigger
      // Video stalls during IMA ads are expected and should not trigger reloads.
      // Removed video_stalls reload trigger - too aggressive.
      if (this.videoStallCount >= this.thresholds.videoStallCount) {
        console.log(`${this.logPrefix} ${this.videoStallCount} video stalls detected (not triggering reload)`);
      }

      // 5. Content load time check
      const avgLoadTime = metrics.content?.averageLoadTime || 0;
      if (avgLoadTime > this.thresholds.contentLoadTimeMs) {
        issues.push({
          type: 'slow_content',
          value: avgLoadTime,
          threshold: this.thresholds.contentLoadTimeMs,
          message: `Average content load time: ${Math.round(avgLoadTime)}ms`
        });
      }

      // Process results
      if (issues.length > 0) {
        this.consecutiveHealthyChecks = 0;
        console.warn(`${this.logPrefix} Health issues detected:`, issues);
        this.handleHealthIssues(issues);
      } else {
        this.consecutiveHealthyChecks++;

        // Reset video stall count periodically when healthy
        if (this.consecutiveHealthyChecks >= 5) {
          this.videoStallCount = 0;
        }

        console.log(`${this.logPrefix} Health check passed (healthy streak: ${this.consecutiveHealthyChecks})`);
      }

    } catch (error) {
      console.error(`${this.logPrefix} Health check failed:`, error);
    }
  }

  /**
   * Handle detected health issues
   */
  handleHealthIssues(issues) {
    // Determine if we need to reload based on issue severity
    // Only memory_pressure is a legitimate reload trigger now.
    // Removed error_spike and video_stalls - too aggressive, especially for CTV/IMA.
    const primaryIssue = issues.find(issue => issue.type === 'memory_pressure');
    const hasCriticalIssue = Boolean(
      primaryIssue &&
      this.memoryPressureStrikeCount >= this.requiredMemoryPressureStrikes
    );

    if (hasCriticalIssue) {
      if (primaryIssue.type === 'memory_pressure' && this._isSamsungTizen()) {
        console.warn(
          `${this.logPrefix} Samsung/Tizen memory pressure is telemetry-only; attempting soft recovery instead of process restart`
        );
        this.triggerSoftRecovery('memory_pressure_tizen');
        return;
      }
      this.scheduleReload(primaryIssue.type);
    } else if (primaryIssue) {
      console.warn(
        `${this.logPrefix} Memory pressure strike ${this.memoryPressureStrikeCount}/${this.requiredMemoryPressureStrikes}; waiting before restart`
      );
    }
  }

  /**
   * Schedule a page reload, waiting for any active ad to complete
   */
  scheduleReload(reason) {
    const isCtvContext = Boolean(window.__TRILL_IS_CTV__ || window.audioStateManager?.isCtvContext);
    if (isCtvContext) {
      if (reason === 'memory_pressure') {
        this.requestCtvProcessRestart(reason);
        return;
      }
      this.triggerSoftRecovery(reason);
      return;
    }

    if (this.reloadScheduled) {
      console.log(`${this.logPrefix} Reload already scheduled`);
      return;
    }

    this.reloadScheduled = true;
    console.warn(`${this.logPrefix} Scheduling reload due to: ${reason}`);

    const attemptReload = () => {
      // Check if an ad is currently being displayed (including IMA ads)
      const isShowingAd = window.minimalAdvertisementOverlay?.isShowingAd || false;
      const isShowingImaAd = window.minimalAdvertisementOverlay?.isShowingImaAd || false;

      if (isShowingAd || isShowingImaAd) {
        console.log(`${this.logPrefix} Ad in progress (ima: ${isShowingImaAd}), delaying reload for 5 seconds...`);
        setTimeout(attemptReload, 5000);
        return;
      }

      // No ad showing, proceed with reload
      console.log(`${this.logPrefix} Reloading page due to: ${reason}`);

      // Log to analytics before reload (if available)
      try {
        if (window.posthog) {
          window.posthog.capture('screen_watchdog_reload', {
            reason: reason,
            timestamp: new Date().toISOString()
          });
        }
      } catch (e) {
        // Ignore analytics errors
      }

      // Small delay to allow analytics to send
      setTimeout(() => {
        window.location.reload();
      }, 100);
    };

    // Start the reload attempt
    attemptReload();
  }

  /**
   * On CTV under memory pressure, escalate to the tizen-agent wrapper so
   * it can drive a true Tizen process exit (frees the WebKit heap, which
   * window.location.reload() does NOT do — Tizen's Chromium build retains
   * DOM references across page navigations). The wrapper validates the
   * message origin and calls tizen.application.exit(); SSSP auto-relaunches.
   *
   * Falls back to window.location.reload() when not embedded in the
   * wrapper (e.g. WebOS or browser dev preview).
   */
  requestCtvProcessRestart(reason) {
    if (this.reloadScheduled) {
      console.log(`${this.logPrefix} CTV process restart already scheduled`);
      return;
    }

    this.reloadScheduled = true;
    console.warn(`${this.logPrefix} Requesting CTV process restart due to: ${reason}`);

    const fireRestart = () => {
      const isShowingAd = window.minimalAdvertisementOverlay?.isShowingAd || false;
      const isShowingImaAd = window.minimalAdvertisementOverlay?.isShowingImaAd || false;

      if (isShowingAd || isShowingImaAd) {
        console.log(`${this.logPrefix} Ad in progress (ima: ${isShowingImaAd}), delaying CTV restart 5s...`);
        setTimeout(fireRestart, 5000);
        return;
      }

      try {
        if (window.posthog) {
          window.posthog.capture('screen_watchdog_reload', {
            reason,
            timestamp: new Date().toISOString(),
            mode: 'ctv_process_restart'
          });
        }
      } catch (e) {
        // Ignore analytics errors
      }

      // Try the wrapper first. If we're inside the tizen-agent iframe,
      // window.parent !== window, and the wrapper listens on the
      // 'trillboards:request-restart' message type at its origin.
      let askedWrapper = false;
      try {
        if (window.parent && window.parent !== window) {
          window.parent.postMessage(
            { type: 'trillboards:request-restart', reason },
            '*'
          );
          askedWrapper = true;
          console.log(`${this.logPrefix} Sent restart request to wrapper`);
        }
      } catch (e) {
        // Cross-origin / SecurityError — postMessage doesn't throw but
        // accessing window.parent === window may; treat as no wrapper.
      }

      // Fallback timer: if we asked the wrapper but it doesn't act
      // (no Tizen API, missing handler, or non-CTV context), the
      // process won't exit. Fall back to in-frame reload so we still
      // get DOM reset. Page reload won't free heap, but it's better
      // than nothing.
      const fallbackDelay = askedWrapper ? 4000 : 100;
      setTimeout(() => {
        try {
          window.location.reload();
        } catch (e) {
          console.error(`${this.logPrefix} Fallback reload failed:`, e);
        }
      }, fallbackDelay);
    };

    fireRestart();
  }

  /**
   * Attempt a soft recovery instead of a full reload (CTV-safe).
   */
  triggerSoftRecovery(reason) {
    const now = Date.now();
    if (now - this.lastSoftRecoveryAt < this.softRecoveryCooldownMs) {
      console.log(`${this.logPrefix} Soft recovery skipped (cooldown active)`);
      return;
    }

    this.lastSoftRecoveryAt = now;
    console.warn(`${this.logPrefix} Soft recovery due to: ${reason}`);

    try {
      if (window.minimalStreamManager?.forceReloadCurrentStream) {
        window.minimalStreamManager.forceReloadCurrentStream(`watchdog:${reason}`);
      } else if (window.minimalStreamManager?.fetchAndUpdateStream) {
        window.minimalStreamManager.fetchAndUpdateStream(true);
      }
      if (window.minimalStreamManager?.kickActiveMedia) {
        window.minimalStreamManager.kickActiveMedia();
      }
      if (window.minimalAdvertisementOverlay?.checkAndShowAds) {
        window.minimalAdvertisementOverlay.checkAndShowAds(true, true);
      }
      if (window.minimalLBar?.refresh) {
        window.minimalLBar.refresh(true);
      }
    } catch (e) {
      // Ignore soft recovery errors
    }
  }

  /**
   * Update thresholds dynamically
   */
  setThresholds(newThresholds) {
    this.thresholds = { ...this.thresholds, ...newThresholds };
    console.log(`${this.logPrefix} Thresholds updated:`, this.thresholds);
  }

  /**
   * Get current watchdog status
   */
  getStatus() {
    return {
      isRunning: this.isRunning,
      thresholds: this.thresholds,
      state: {
        lastErrorCount: this.lastErrorCount,
        lastOnlineTime: this.lastOnlineTime,
        videoStallCount: this.videoStallCount,
        reloadScheduled: this.reloadScheduled,
        consecutiveHealthyChecks: this.consecutiveHealthyChecks
      }
    };
  }
}

export default HealthWatchdog;
